import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import QuickLinksModal from "@/components/QuickLinksModal";
import { ListDashes } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [qlOpen, setQlOpen] = useState(false);
    const { login, error } = useAuth();
    const navigate = useNavigate();
    const [branding, setBranding] = useState(null);
    const [landing, setLanding] = useState({
        hero_title_1: "点検を標準化し、",
        hero_title_2: "安全を仕組みに",
        hero_title_3: "変える。",
        hero_sub: "法人向け車両管理・点検・運行支援 SaaS。現場から経営まで、1つのプラットフォームで。",
    });

    useEffect(() => {
        api.get("/settings/public")
            .then((r) => {
                const b = r.data?.branding || {};
                const l = r.data?.landing || {};
                setBranding({
                    brand_name: b.brand_name || "PRC CMC",
                    brand_sub: b.brand_sub || "FLEET CONTROL OS",
                    logo_data_url: b.logo_data_url || null,
                    hero_image_url: b.hero_image_url || null,
                });
                setLanding((prev) => ({
                    hero_title_1: l.hero_title_1 || prev.hero_title_1,
                    hero_title_2: l.hero_title_2 || prev.hero_title_2,
                    hero_title_3: l.hero_title_3 || prev.hero_title_3,
                    hero_sub: l.hero_sub || prev.hero_sub,
                }));
            })
            .catch(() => setBranding({ brand_name: "PRC CMC", brand_sub: "FLEET CONTROL OS", logo_data_url: null, hero_image_url: null }));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const ok = await login(email, password);
        setLoading(false);
        if (ok) {
            toast.success("ログインしました");
            navigate("/");
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
            <div className="hidden lg:flex items-center justify-center text-white p-12 relative border-r border-[var(--prc-border)] overflow-hidden bg-[var(--prc-surface)]">
                {branding?.hero_image_url && (
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${branding.hero_image_url})` }} />
                )}
                <div className="absolute inset-0 bg-black/60" />
                <div className="absolute top-8 left-8 flex items-center gap-2.5 z-10" style={{ visibility: branding ? "visible" : "hidden" }}>
                    {branding?.logo_data_url ? (
                        <img src={branding.logo_data_url} alt="logo" className="h-8" />
                    ) : (
                        <span className="w-2 h-2 rounded-full bg-[var(--prc-green)]" style={{ boxShadow: "0 0 8px var(--prc-green)" }} />
                    )}
                    <div>
                        <p className="font-mono text-[14px] font-extrabold tracking-[0.1em] text-[var(--prc-ink-2)]">{branding?.brand_name}</p>
                        <p className="prc-label-overline mt-0.5">{branding?.brand_sub}</p>
                    </div>
                </div>
                <div className="relative max-w-md z-10">
                    <p className="prc-label-overline !text-[var(--prc-green)] mb-5">// MISSION · {branding?.brand_name || "PRC CMC"}</p>
                    <h2 className="text-[1.575rem] sm:text-[2rem] lg:text-[2.7rem] font-bold tracking-tight leading-[1.05]">
                        {landing.hero_title_1}<br />
                        <span className="text-[var(--prc-green)]">{landing.hero_title_2}</span>{landing.hero_title_3}
                    </h2>
                    <p className="mt-6 text-lg text-white/80 leading-relaxed">{landing.hero_sub}</p>
                </div>
                <div className="absolute bottom-8 left-8 text-xs text-white/50 font-mono z-10" style={{ visibility: branding ? "visible" : "hidden" }}>
                    © {new Date().getFullYear()} {branding?.brand_name}
                </div>
                <button onClick={() => setQlOpen(true)} className="absolute top-8 right-8 prc-btn-ghost inline-flex items-center gap-1.5 !text-white !border-white/30 z-10" data-testid="login-quick-links-btn">
                    <ListDashes size={16} /> クイックリンク
                </button>
            </div>
            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-md">
                    <p className="prc-label-overline mb-2 !text-[var(--prc-green)]">// SIGN IN</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight text-[var(--prc-ink-2)]">アカウントにログイン</h1>
                    <p className="mt-2 text-sm text-[var(--prc-muted)]">
                        登録がお済みでない方は <Link to="/register" className="font-semibold text-[var(--prc-green)] hover:underline" data-testid="login-goto-register">新規登録</Link>
                    </p>

                    <div className="mt-8 space-y-4">
                        <div>
                            <label className="prc-label-overline block mb-1.5">ログインID または メールアドレス</label>
                            <input
                                type="text"
                                autoCapitalize="off"
                                autoCorrect="off"
                                spellCheck="false"
                                className="prc-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <label className="prc-label-overline block mb-1.5">パスワード</label>
                            <input
                                type="password"
                                className="prc-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password-input"
                            />
                        </div>
                    </div>

                    {error && <p className="mt-4 text-sm text-[var(--prc-red)]" data-testid="login-error">{error}</p>}

                    <button
                        type="submit"
                        disabled={loading}
                        className="prc-btn-primary w-full mt-6"
                        data-testid="login-submit-btn"
                    >
                        {loading ? "サインイン中…" : "サインイン"}
                    </button>
                </form>
            </div>
            <QuickLinksModal open={qlOpen} onClose={() => setQlOpen(false)} />
        </div>
    );
}
